/**
 * Web-specific wrapper for liability waiver services
 * Pre-configured with web Supabase client
 * Inlined from app-shared to avoid webpack import restrictions
 */

import { supabase } from '../lib/supabase';
import type {
  DamageWaiverTemplate,
  DamageWaiverTemplateField,
  DamageWaiverTemplateFieldInsert,
  DamageWaiverSignature,
  DamageWaiverTemplateWithFields,
  WaiverRequirementCheck,
  SignWaiverInput,
  DamageWaiverTemplateInsert,
  DamageWaiverTemplateUpdate,
  DamageWaiverSignatureInsert,
} from '../types/damageWaiver.types';
import type { Json } from '../types/supabase';

const BUCKET_NAME = 'damage-waivers';

/**
 * Upload a waiver template file to storage
 */
async function uploadWaiverFile(
  fileData: Blob | ArrayBuffer | Uint8Array,
  filePath: string,
  contentType: string
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKET_NAME)
    .upload(filePath, fileData, {
      contentType,
      upsert: true,
    });

  if (error) {
    throw new Error(`Failed to upload waiver file: ${error.message}`);
  }

  return data.path;
}

/**
 * Get a signed URL for viewing a waiver file
 */
export async function getWaiverFileUrl(
  filePath: string,
  expiresIn: number = 3600
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKET_NAME)
    .createSignedUrl(filePath, expiresIn);

  if (error) {
    throw new Error(`Failed to get signed URL: ${error.message}`);
  }

  return data.signedUrl;
}

// ─── Compliance types ─────────────────────────────────────────────────────────

export interface WaiverComplianceMember {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email?: string | null;
}

export interface WaiverComplianceStats {
  adults: { total: number; signed: number; unsigned: WaiverComplianceMember[] };
  children: { total: number; signed: number; unsigned: WaiverComplianceMember[] };
}

export interface CompanySignedWaiver {
  id: string;
  signed_at: string;
  signed_by: string;
  sign_method: string;
  user_id: string | null;
  child_profile_id: string | null;
  signed_pdf_path: string;
  template_name: string | null;
}

/**
 * Compliance summary: adult and child waiver acceptance rates for a company.
 */
export async function getWaiverComplianceStats(
  companyId: string
): Promise<WaiverComplianceStats> {
  // Adults — from user_company_relationships joined with user_profiles
  const { data: adultRows, error: adultErr } = await supabase
    .from('user_company_relationships')
    .select(`
      user_id, damage_waiver_accepted,
      user_profiles!user_company_relationships_user_id_fkey(id, first_name, last_name, email)
    `)
    .eq('company_id', companyId);

  if (adultErr) throw new Error(`Failed to fetch compliance stats: ${adultErr.message}`);

  const rows = (adultRows || []) as any[];
  const unsignedAdults: WaiverComplianceMember[] = rows
    .filter(r => !r.damage_waiver_accepted)
    .map(r => ({
      id: r.user_id,
      first_name: r.user_profiles?.first_name ?? null,
      last_name: r.user_profiles?.last_name ?? null,
      email: r.user_profiles?.email ?? null,
    }));

  // Children — all child_profiles whose parent is in the company
  const parentIds = rows.map(r => r.user_id).filter(Boolean);
  let childRows: any[] = [];
  if (parentIds.length > 0) {
    const { data: kids } = await supabase
      .from('child_profiles')
      .select('id, first_name, last_name, damage_waiver_accepted')
      .in('parent_id', parentIds);
    childRows = kids || [];
  }

  const unsignedChildren: WaiverComplianceMember[] = childRows
    .filter(c => !c.damage_waiver_accepted)
    .map(c => ({ id: c.id, first_name: c.first_name, last_name: c.last_name }));

  return {
    adults: {
      total: rows.length,
      signed: rows.filter(r => r.damage_waiver_accepted).length,
      unsigned: unsignedAdults,
    },
    children: {
      total: childRows.length,
      signed: childRows.filter(c => c.damage_waiver_accepted).length,
      unsigned: unsignedChildren,
    },
  };
}

/**
 * All signed waivers for a company — for the owner compliance view.
 */
export async function getAllCompanySignedWaivers(
  companyId: string
): Promise<CompanySignedWaiver[]> {
  const { data, error } = await supabase
    .from('damage_waiver_signatures')
    .select(`
      id, signed_at, signed_by, sign_method, user_id, child_profile_id, signed_pdf_path,
      damage_waiver_templates!damage_waiver_signatures_template_id_fkey(name)
    `)
    .eq('company_id', companyId)
    .order('signed_at', { ascending: false });

  if (error) throw new Error(`Failed to fetch signed waivers: ${error.message}`);

  return ((data || []) as any[]).map(row => ({
    id: row.id,
    signed_at: row.signed_at,
    signed_by: row.signed_by,
    sign_method: row.sign_method,
    user_id: row.user_id,
    child_profile_id: row.child_profile_id,
    signed_pdf_path: row.signed_pdf_path,
    template_name: row.damage_waiver_templates?.name ?? null,
  }));
}

/**
 * Upload a waiver template and create database record
 */
export async function uploadWaiverTemplate(
  fileData: Blob | ArrayBuffer | Uint8Array,
  companyId: string,
  name: string,
  description?: string,
  contentType: string = 'application/pdf',
  requiresChild: boolean = false,
  waiverCategory: 'adult' | 'child' | 'family' | 'general' = 'general'
): Promise<DamageWaiverTemplate> {
  const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(7)}`;
  const fileExtension = contentType.includes('pdf') ? 'pdf' : (contentType.split('/')[1] || 'pdf');
  const storagePath = `templates/${companyId}/${tempId}.${fileExtension}`;

  await uploadWaiverFile(fileData, storagePath, contentType);

  const insertData: DamageWaiverTemplateInsert & { waiver_category: string } = {
    company_id: companyId,
    name,
    description: description || null,
    storage_path: storagePath,
    is_active: true,
    requires_child: requiresChild,
    waiver_category: waiverCategory,
  };

  const { data, error } = await supabase
    .from('damage_waiver_templates')
    .insert(insertData)
    .select()
    .single();

  if (error) {
    await supabase.storage.from(BUCKET_NAME).remove([storagePath]);
    throw new Error(`Failed to create template: ${error.message}`);
  }

  const template = data as unknown as DamageWaiverTemplate;
  
  const finalStoragePath = `templates/${companyId}/${template.id}.${fileExtension}`;
  if (finalStoragePath !== storagePath) {
    const { error: copyError } = await supabase.storage
      .from(BUCKET_NAME)
      .copy(storagePath, finalStoragePath);
    
    if (copyError) {
      console.warn('Failed to rename template file:', copyError);
    } else {
      await supabase
        .from('damage_waiver_templates')
        .update({ storage_path: finalStoragePath })
        .eq('id', template.id);
      
      await supabase.storage.from(BUCKET_NAME).remove([storagePath]);
      template.storage_path = finalStoragePath;
    }
  }

  return template;
}

/**
 * List all waiver templates for a company
 */
export async function listCompanyWaiverTemplates(
  companyId: string
): Promise<DamageWaiverTemplate[]> {
  const { data, error } = await supabase
    .from('damage_waiver_templates')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to list templates: ${error.message}`);
  }

  return (data || []) as unknown as DamageWaiverTemplate[];
}

/**
 * Get a single waiver template with its fields
 */
export async function getWaiverTemplate(
  templateId: string
): Promise<DamageWaiverTemplateWithFields | null> {
  const { data: template, error: templateError } = await supabase
    .from('damage_waiver_templates')
    .select('*')
    .eq('id', templateId)
    .single();

  if (templateError) {
    throw new Error(`Failed to get template: ${templateError.message}`);
  }

  if (!template) {
    return null;
  }

  const { data: fields, error: fieldsError } = await supabase
    .from('damage_waiver_template_fields')
    .select('*')
    .eq('template_id', templateId)
    .order('order', { ascending: true });

  if (fieldsError) {
    throw new Error(`Failed to get template fields: ${fieldsError.message}`);
  }

  return {
    ...(template as DamageWaiverTemplate),
    fields: (fields || []) as unknown as DamageWaiverTemplateField[],
  };
}

/**
 * Update a waiver template
 */
export async function updateTemplate(
  templateId: string,
  updates: DamageWaiverTemplateUpdate
): Promise<DamageWaiverTemplate> {
  const { data, error } = await supabase
    .from('damage_waiver_templates')
    .update(updates)
    .eq('id', templateId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update template: ${error.message}`);
  }

  return data as DamageWaiverTemplate;
}

/**
 * Delete a waiver template (cascades to fields)
 */
export async function deleteTemplate(
  templateId: string
): Promise<void> {
  const template = await getWaiverTemplate(templateId);
  if (!template) {
    throw new Error('Template not found');
  }

  const { error } = await supabase
    .from('damage_waiver_templates')
    .delete()
    .eq('id', templateId);

  if (error) {
    throw new Error(`Failed to delete template: ${error.message}`);
  }

  if (template.storage_path) {
    await supabase.storage.from(BUCKET_NAME).remove([template.storage_path]);
  }
}

/**
 * Save template fields (replaces all fields for the template)
 */
export async function saveTemplateFields(
  templateId: string,
  fields: Omit<DamageWaiverTemplateFieldInsert, 'template_id'>[]
): Promise<DamageWaiverTemplateField[]> {
  const { error: deleteError } = await supabase
    .from('damage_waiver_template_fields')
    .delete()
    .eq('template_id', templateId);

  if (deleteError) {
    throw new Error(`Failed to delete existing fields: ${deleteError.message}`);
  }

  if (fields.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from('damage_waiver_template_fields')
    .insert(
      fields.map(f => ({
        ...f,
        template_id: templateId,
        field_type: f.field_type as string,
        meta: (f.meta || {}) as Json,
      }))
    )
    .select();

  if (error) {
    throw new Error(`Failed to save fields: ${error.message}`);
  }

  return (data || []) as unknown as DamageWaiverTemplateField[];
}

/**
 * Get template fields
 */
export async function getTemplateFields(
  templateId: string
): Promise<DamageWaiverTemplateField[]> {
  const { data, error } = await supabase
    .from('damage_waiver_template_fields')
    .select('*')
    .eq('template_id', templateId)
    .order('order', { ascending: true });

  if (error) {
    throw new Error(`Failed to get fields: ${error.message}`);
  }

  return (data || []) as unknown as DamageWaiverTemplateField[];
}

/**
 * Get the active template for a company
 */
export async function getActiveTemplateForCompany(
  companyId: string,
  requiresChild?: boolean
): Promise<DamageWaiverTemplate | null> {
  let query = supabase
    .from('damage_waiver_templates')
    .select('*')
    .eq('company_id', companyId)
    .eq('is_active', true);

  if (requiresChild !== undefined) {
    query = query.eq('requires_child', requiresChild);
  }

  const { data, error } = await query.order('created_at', { ascending: false }).limit(1).maybeSingle();

  if (error) {
    throw new Error(`Failed to get active template: ${error.message}`);
  }

  return (data as unknown as DamageWaiverTemplate) || null;
}

/**
 * Sign a waiver and save the signed PDF
 * Uses Edge Function to generate PDF with field overlays and signature
 */
export async function signWaiver(
  input: SignWaiverInput
): Promise<DamageWaiverSignature> {
  const { templateId, companyId, userId, childProfileId, signatureData, signedBy, signMethod, autoFilledFields } = input;

  // APP-LEVEL VALIDATION: Prevent admins/owners from signing waivers for other users
  // Get current user ID
  const { data: { user: currentUser } } = await supabase.auth.getUser();
  if (!currentUser?.id) {
    throw new Error('User not authenticated');
  }

  // Validate userId: if provided, must match current user (users can only sign for themselves)
  if (userId && userId !== currentUser.id) {
    throw new Error('You can only sign waivers for yourself. Students must sign their own waivers.');
  }

  // Validate childProfileId: if provided, verify current user is the parent
  if (childProfileId) {
    const { data: childProfile, error: childError } = await supabase
      .from('child_profiles')
      .select('parent_id')
      .eq('id', childProfileId)
      .single();

    if (childError || !childProfile) {
      throw new Error('Child profile not found');
    }

    if (childProfile.parent_id !== currentUser.id) {
      throw new Error('You can only sign waivers for your own children.');
    }
  }

  // Get template to verify it exists and get fields for mapping
  const template = await getWaiverTemplate(templateId);
  if (!template) {
    throw new Error('Template not found');
  }

  // Get user and company info for auto-fill if not provided
  // Profile data structure from Supabase - varies by query
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let userProfile: any = null;
  // Company data structure from Supabase - varies by query
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let company: any = null;
  // Child profile data structure from Supabase - varies by query
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let childProfile: any = null;

  if (userId) {
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('first_name, last_name, email, phone')
      .eq('id', userId)
      .single();
    userProfile = profile;
  }

  if (childProfileId) {
    const { data: child } = await supabase
      .from('child_profiles')
      .select('first_name, last_name')
      .eq('id', childProfileId)
      .single();
    childProfile = child;
  }

  if (companyId) {
    const { data: comp } = await supabase
      .from('companies')
      .select('name, address, address2, city, state, zip')
      .eq('id', companyId)
      .single();
    company = comp;
  }

  // Build autoFilledFields map if not provided
  const finalAutoFilledFields: Record<string, string> = autoFilledFields || {};

  // If autoFilledFields not provided, generate from template fields
  if (!autoFilledFields && template.fields) {
    for (const field of template.fields) {
      const autoFillSource = field.meta?.autoFillSource;
      if (!autoFillSource) continue;

      let value = '';
      switch (autoFillSource) {
        case 'signer_name':
          // Signer name = parent/user name (who is signing)
          value = userProfile ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() : '';
          break;
        case 'student_name':
          // Student name = child name (if signing for a child) OR user name (if signing for themselves)
          if (childProfileId && childProfile) {
            value = `${childProfile.first_name || ''} ${childProfile.last_name || ''}`.trim();
          } else {
            value = userProfile ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() : '';
          }
          break;
        case 'user_name': // Backward compatibility - maps to signer_name
          value = userProfile ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() : '';
          break;
        case 'user_email':
          value = userProfile?.email || '';
          break;
        case 'user_phone':
          value = userProfile?.phone || '';
          break;
        case 'current_date':
          value = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
          break;
        case 'gym_name':
          value = company?.name || '';
          break;
        case 'gym_address': {
          const addrParts = [];
          if (company?.address) addrParts.push(company.address);
          if (company?.address2) addrParts.push(company.address2);
          if (company?.city) addrParts.push(company.city);
          if (company?.state) addrParts.push(company.state);
          if (company?.zip) addrParts.push(company.zip);
          value = addrParts.join(', ');
          break;
        }
      }
      
      if (value) {
        finalAutoFilledFields[field.id] = value;
      }
    }
  }

  // Call Edge Function to generate signed PDF
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Not authenticated');
  }

  const { data: edgeFunctionData, error: edgeError } = await supabase.functions.invoke(
    'generate-signed-waiver-pdf',
    {
      body: {
        templateId,
        signatureData,
        autoFilledFields: finalAutoFilledFields,
        userId,
        childProfileId,
        companyId,
      },
    }
  );

  if (edgeError || !edgeFunctionData?.signedPdfPath) {
    // Fallback to V1 behavior if Edge Function fails
    console.warn('Edge Function failed, using fallback:', edgeError);
    
    const signatureId = `sig-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    const signatureImagePath = `signed/${companyId}/${userId || childProfileId}/${signatureId}.png`;
    
    // Convert base64 to blob/arraybuffer
    const base64Data = signatureData.includes(',') ? signatureData.split(',')[1] : signatureData;
    const binaryString = window.atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Upload signature image
    await uploadWaiverFile(bytes.buffer, signatureImagePath, 'image/png');
    const signedPdfPath = signatureImagePath.replace('.png', '.pdf');
    
    // Create signature record
    const insertData: DamageWaiverSignatureInsert = {
      template_id: templateId,
      company_id: companyId,
      user_id: userId || null,
      child_profile_id: childProfileId || null,
      signed_pdf_path: signedPdfPath,
      signed_by: signedBy,
      sign_method: signMethod,
    };

    const { data, error } = await supabase
      .from('damage_waiver_signatures')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create signature record: ${error.message}`);
    }

    // Update user_company_relationships or child_profiles
    if (userId && !childProfileId) {
      const { error: updateError } = await supabase
        .from('user_company_relationships')
        .update({ damage_waiver_accepted: true })
        .eq('user_id', userId)
        .eq('company_id', companyId);

      if (updateError) {
        console.warn('Failed to update user_company_relationships:', updateError);
        // Don't throw - signature record is created, this is just a flag update
      }
    } else if (childProfileId) {
      const { error: updateError } = await supabase
        .from('child_profiles')
        .update({ damage_waiver_accepted: true })
        .eq('id', childProfileId);

      if (updateError) {
        console.warn('Failed to update child_profiles:', updateError);
        // Don't throw - signature record is created, this is just a flag update
      }
    }

    return data as unknown as DamageWaiverSignature;
  }

  // Edge Function succeeded - use the generated PDF path
  const signedPdfPath = edgeFunctionData.signedPdfPath;

  // Create signature record
  const insertData: DamageWaiverSignatureInsert = {
    template_id: templateId,
    company_id: companyId,
    user_id: userId || null,
    child_profile_id: childProfileId || null,
    signed_pdf_path: signedPdfPath,
    signed_by: signedBy,
    sign_method: signMethod,
  };

  const { data, error } = await supabase
    .from('damage_waiver_signatures')
    .insert(insertData)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to create signature record: ${error.message}`);
  }

  // Update user_company_relationships or child_profiles
  if (userId && !childProfileId) {
    const { error: updateError } = await supabase
      .from('user_company_relationships')
      .update({ damage_waiver_accepted: true })
      .eq('user_id', userId)
      .eq('company_id', companyId);

    if (updateError) {
      console.warn('Failed to update user_company_relationships:', updateError);
      // Don't throw - signature record is created, this is just a flag update
    }
  } else if (childProfileId) {
    const { error: updateError } = await supabase
      .from('child_profiles')
      .update({ damage_waiver_accepted: true })
      .eq('id', childProfileId);

    if (updateError) {
      console.warn('Failed to update child_profiles:', updateError);
      // Don't throw - signature record is created, this is just a flag update
    }
  }

  return data as unknown as DamageWaiverSignature;
}

/**
 * Get signed waivers for a user or child profile
 */
export async function getSignedWaiversForProfile(options: {
  userId?: string;
  childProfileId?: string;
  companyId?: string;
}): Promise<DamageWaiverSignature[]> {
  let query = supabase.from('damage_waiver_signatures').select('*');

  if (options.userId) {
    query = query.eq('user_id', options.userId);
  }

  if (options.childProfileId) {
    query = query.eq('child_profile_id', options.childProfileId);
  }

  if (options.companyId) {
    query = query.eq('company_id', options.companyId);
  }

  const { data, error } = await query.order('signed_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to get signed waivers: ${error.message}`);
  }

  return (data || []) as unknown as DamageWaiverSignature[];
}

/**
 * Get signed URL for viewing a signed waiver PDF
 */
export async function getSignedWaiverUrl(
  signatureId: string,
  expiresIn: number = 3600
): Promise<string> {
  const { data: signature, error: fetchError } = await supabase
    .from('damage_waiver_signatures')
    .select('signed_pdf_path')
    .eq('id', signatureId)
    .single();

  if (fetchError || !signature) {
    throw new Error(`Failed to get signature: ${fetchError?.message || 'Not found'}`);
  }

  const sig = signature as unknown as { signed_pdf_path: string };
  return getWaiverFileUrl(sig.signed_pdf_path, expiresIn);
}

/**
 * Check if a waiver is required and return the active template
 */
export async function ensureWaiverOnFile(options: {
  userId: string;
  companyId: string;
  childProfileId?: string;
}): Promise<WaiverRequirementCheck> {
  const { userId, companyId, childProfileId } = options;

  if (childProfileId) {
    const { data: childProfile, error: childError } = await supabase
      .from('child_profiles')
      .select('damage_waiver_accepted')
      .eq('id', childProfileId)
      .single();

    if (childError) {
      throw new Error(`Failed to check child profile: ${childError.message}`);
    }

    const child = childProfile as unknown as { damage_waiver_accepted: boolean } | null;
    if (child?.damage_waiver_accepted) {
      return {
        isRequired: false,
        templateId: null,
        template: null,
      };
    }

    // Get active template for child - first try child-specific, then fall back to adult template
    let template = await getActiveTemplateForCompany(companyId, true);
    
    // If no child-specific template exists, fall back to adult template
    if (!template) {
      template = await getActiveTemplateForCompany(companyId, false);
    }
    
    return {
      isRequired: !!template,
      templateId: template?.id || null,
      template,
      message: template ? 'Minor waiver signature required' : undefined,
    };
  } else {
    const { data: relationship, error: relError } = await supabase
      .from('user_company_relationships')
      .select('damage_waiver_accepted')
      .eq('user_id', userId)
      .eq('company_id', companyId)
      .single();

    if (relError) {
      throw new Error(`Failed to check user relationship: ${relError.message}`);
    }

    const rel = relationship as unknown as { damage_waiver_accepted: boolean } | null;
    if (rel?.damage_waiver_accepted) {
      return {
        isRequired: false,
        templateId: null,
        template: null,
      };
    }

    const template = await getActiveTemplateForCompany(companyId, false);
    return {
      isRequired: !!template,
      templateId: template?.id || null,
      template,
      message: template ? 'Liability waiver signature required' : undefined,
    };
  }
}
